import { Link } from "react-router-dom";
import { Canvas, useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";
import "./Hero.css";

import deviceImg from "../assets/Angle3.png";
import PortableIcon from "../assets/PortableStorage.png";
import WifiIcon from "../assets/Wifi.png";
import SecureIcon from "../assets/Secure.png";

function ConnectivityPlexus({ numNodes = 60, connectionThreshold = 3.5 }) {
  const groupRef = useRef();
  const nodeRefs = useRef([]);

  const { nodeMeshes, lineGeometryData } = useMemo(() => {
    const positions = [];
    const nodeColors = [];

    const midnight = new THREE.Color("#0a1f44");
    const glowCyan = new THREE.Color("#7ce8ff");
    const glowViolet = new THREE.Color("#c4b5ff");
    const glowPeach = new THREE.Color("#ffb4d3");
    const lineBaseColor = new THREE.Color("#5ad4ff");

    for (let i = 0; i < numNodes; i++) {
      positions.push(new THREE.Vector3(
        THREE.MathUtils.randFloatSpread(10),
        THREE.MathUtils.randFloatSpread(10),
        THREE.MathUtils.randFloatSpread(10) - 3
      ));
      const rnd = Math.random();
      if      (rnd < 0.6) nodeColors.push(glowCyan.clone().lerp(midnight, Math.random() * 0.2));
      else if (rnd < 0.85) nodeColors.push(glowViolet.clone());
      else                 nodeColors.push(glowPeach.clone());
    }

    const verts = [];
    const cols  = [];
    for (let i = 0; i < numNodes; i++) {
      for (let j = i + 1; j < numNodes; j++) {
        const dist = positions[i].distanceTo(positions[j]);
        if (dist < connectionThreshold) {
          verts.push(
            positions[i].x, positions[i].y, positions[i].z,
            positions[j].x, positions[j].y, positions[j].z
          );
          const cRnd = Math.random();
          let chosen = lineBaseColor.clone();
          if      (cRnd < 0.7)          chosen = lineBaseColor.clone();
          else if (cRnd < 0.85)         chosen = glowViolet.clone();
          else                           chosen = glowPeach.clone();
          cols.push(
            chosen.r, chosen.g, chosen.b,
            chosen.r, chosen.g, chosen.b
          );
        }
      }
    }

    return {
      nodeMeshes: positions.map((pos, i) => ({
        position: [pos.x, pos.y, pos.z],
        color: nodeColors[i],
        id: i
      })),
      lineGeometryData: {
        positions: new Float32Array(verts),
        colors:    new Float32Array(cols)
      }
    };
  }, [numNodes, connectionThreshold]);

  useFrame((state, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.02;
      groupRef.current.rotation.x += delta * 0.005;

      const tx = state.mouse.x * 1.5;
      const ty = state.mouse.y * 1.5;
      groupRef.current.position.x = THREE.MathUtils.lerp(groupRef.current.position.x, tx, 0.05);
      groupRef.current.position.y = THREE.MathUtils.lerp(groupRef.current.position.y, ty, 0.05);
    }

    nodeMeshes.forEach((node, i) => {
      const mesh = nodeRefs.current[i];
      if (mesh) {
        const t = state.clock.elapsedTime;
        mesh.position.y += Math.sin(t * 0.5 + i * 0.5) * 0.002;
        mesh.scale.setScalar(1 + Math.sin(t * 0.3 + i * 0.7) * 0.05);
      }
    });
  });

  return (
    <group ref={groupRef}>
      {nodeMeshes.map(n => (
        <mesh
          key={n.id}
          position={n.position}
          ref={el => { nodeRefs.current[n.id] = el; }}
        >
          <sphereGeometry args={[0.08, 16, 16]} />
          <meshBasicMaterial color={n.color} />
        </mesh>
      ))}

      {lineGeometryData.positions.length > 0 && (
        <lineSegments>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              array={lineGeometryData.positions}
              itemSize={3}
              count={lineGeometryData.positions.length / 3}
            />
            <bufferAttribute
              attach="attributes-color"
              array={lineGeometryData.colors}
              itemSize={3}
              count={lineGeometryData.colors.length / 3}
            />
          </bufferGeometry>
          <lineBasicMaterial
            vertexColors
            blending={THREE.AdditiveBlending}
            transparent
            opacity={0.65}
            depthWrite={false}
          />
        </lineSegments>
      )}
    </group>
  );
}

function DynamicPlexusBackground() {
  return (
    <>
      <ambientLight intensity={0.1} />
      <ConnectivityPlexus numNodes={70} connectionThreshold={3.2} />
      <FloatingRings />
      <GlowParticles count={420} />
    </>
  );
}

function FloatingRings() {
  const ringsRef = useRef();

  useFrame((state, delta) => {
    if (!ringsRef.current) return;
    ringsRef.current.rotation.y += delta * 0.05;
    ringsRef.current.position.z = THREE.MathUtils.lerp(
      ringsRef.current.position.z,
      -2 + Math.sin(state.clock.elapsedTime * 0.2),
      0.02
    );
  });

  const palette = ["#6ae4ff", "#c084fc", "#ff9ecd"];

  return (
    <group ref={ringsRef}>
      {[0, 1, 2].map((idx) => (
        <mesh
          key={idx}
          rotation={[Math.PI / 2, 0, idx * 0.35]}
          scale={1 + idx * 0.2}
          position={[0, idx * 0.3, -1 - idx * 0.4]}
        >
          <torusGeometry args={[3.5 + idx * 0.8, 0.04 + idx * 0.02, 32, 256]} />
          <meshBasicMaterial
            color={palette[idx]}
            transparent
            opacity={0.35 - idx * 0.06}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
}

function GlowParticles({ count = 300 }) {
  const groupRef = useRef();
  const { positions, colors } = useMemo(() => {
    const posArr = new Float32Array(count * 3);
    const colorArr = new Float32Array(count * 3);
    const palette = ["#fde047", "#c4b5fd", "#6ee7b7", "#93c5fd", "#fca5a5", "#f9a8d4"].map(
      (hex) => new THREE.Color(hex),
    );

    for (let i = 0; i < count; i++) {
      posArr[i * 3 + 0] = THREE.MathUtils.randFloatSpread(18);
      posArr[i * 3 + 1] = THREE.MathUtils.randFloatSpread(12);
      posArr[i * 3 + 2] = THREE.MathUtils.randFloatSpread(10) - 4;

      const color = palette[Math.floor(Math.random() * palette.length)];
      colorArr[i * 3 + 0] = color.r;
      colorArr[i * 3 + 1] = color.g;
      colorArr[i * 3 + 2] = color.b;
    }

    return { positions: posArr, colors: colorArr };
  }, [count]);

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y += delta * 0.01;
    groupRef.current.rotation.x += delta * 0.005;
  });

  return (
    <points ref={groupRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          array={positions}
          count={count}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          array={colors}
          count={count}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.09}
        vertexColors
        transparent
        opacity={0.9}
        depthWrite={false}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

export default function Hero() {
  return (
    <section className="hero">
      <div className="hero-3d-background">
        <Canvas camera={{ position: [0, 1, 12], fov: 55 }}>
          <DynamicPlexusBackground />
        </Canvas>
      </div>
      <div className="hero-gradient-overlay" />

      <div className="hero-content-overlay">
        <div className="hero-text">
          <p className="hero-kicker">Edge-ready • Cloudless • Instant sharing</p>
          <h1 className="hero-title">
            <span className="hero-headline">
              Build your own <span className="highlight">secure broadcast bubble</span>
              <br />
              anywhere your team needs it.
            </span>
          </h1>
          <p className="hero-description">
            ChipServer spins up a private Wi-Fi network with on-device storage so crews can
            capture, preview, and share without touching the public cloud.
          </p>
          <div className="hero-metrics">
            <div>
              <strong>5 min</strong>
              <span>from case to broadcast</span>
            </div>
            <div>
              <strong>1 TB</strong>
              <span>encrypted edge storage</span>
            </div>
            <div>
              <strong>200 ft</strong>
              <span>secure sharing radius</span>
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-visual__halo" />
          <div className="hero-visual__card">
            <img src={deviceImg} alt="ChipServer Device" />
            <div className="hero-visual__status">
              <span className="status-dot" />
              Broadcasting locally
            </div>
          </div>
          <div className="hero-buttons hero-visual__actions">
            <Link to="/store/" className="btn btn-primary">
              Get Started
            </Link>
            <a
              href="#pricing"
              className="btn btn-ghost"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("pricing").scrollIntoView({ behavior: "smooth" });
              }}
            >
              See Pricing
            </a>
          </div>
        </div>
      </div>
      <div className="hero-badges">
        <div className="badge">
          <img src={PortableIcon} alt="Portable Storage" />
          <div>
            <p>Portable Storage</p>
            <small>Carry offline libraries anywhere.</small>
          </div>
        </div>
        <div className="badge">
          <img src={WifiIcon} alt="No Internet Needed" />
          <div>
            <p>No Internet Needed</p>
            <small>Local Wi-Fi mesh in seconds.</small>
          </div>
        </div>
        <div className="badge">
          <img src={SecureIcon} alt="Secure Sharing" />
          <div>
            <p>Secure Sharing</p>
            <small>Zero-cloud, encrypted handoffs.</small>
          </div>
        </div>
      </div>
    </section>
  );
}
